import { Box, CircularProgress } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { DETAIL_PANE_WIDTH } from "../../theme/v2Tokens";
import { useInteractionMetadata, useFileUrl } from "../../../hooks/useInteraction";
import { useTranscript } from "../../../hooks/useTranscription";
import { useGetSummary, useSummaryPolling } from "../../../hooks/useSummary";
import { SummaryStatus } from "../../../types/summary/Summary";
import { usePopoutManager } from "../../hooks/usePopoutManager";
import ArchieContextBar from "../ArchiePanel/ArchieContextBar";
import WaveformPlayer from "../Waveform/WaveformPlayer";
import type { PlayerHandle } from "../Waveform/WaveformPlayer";
import DetailHeader from "./DetailHeader";
import DetailPaneStub from "./DetailPaneStub";
import DetailTabs, { TabId } from "./DetailTabs";
import MetadataTab from "./MetadataTab";
import SummaryTab from "./SummaryTab";
import TranscriptTab from "./TranscriptTab";

interface DetailPaneProps {
  readonly interactionId: string;
  readonly customerId: string;
  readonly interactionType: string;
  readonly onClose: () => void;
  readonly initialTab?: TabId;
}

/** Interaction types that support audio/video playback. */
const MEDIA_TYPES = new Set(["Audio", "Video", "AudioAndVideo"]);

function formatDuration(ms?: number): string {
  if (typeof ms !== "number" || !ms) return "";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatTimestamp(value?: string): string {
  if (!value || typeof value !== "string") return "";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function DetailPane({
  interactionId,
  customerId,
  interactionType,
  onClose,
  initialTab,
}: DetailPaneProps) {
  const [activeTab, setActiveTab] = useState<TabId>(initialTab ?? "transcript");
  const [currentTimeSeconds, setCurrentTimeSeconds] = useState<number>(0);

  const manager = usePopoutManager({ windowType: "main", interactionId });
  const playerRef = useRef<PlayerHandle>(null);

  // Local state for immediate stub display — this is the primary control
  const [locallyPopped, setLocallyPopped] = useState(false);
  const [resumeTime, setResumeTime] = useState<number | null>(null);
  const popoutWindowRef = useRef<Window | null>(null);

  // Detect external close (browser X, Alt+F4) — worker says no popout but we still show stub
  useEffect(() => {
    if (locallyPopped && !manager.isPopped(interactionId)) {
      // Give the pop-out a moment to register before assuming it was closed
      const timer = setTimeout(() => {
        if (!manager.isPopped(interactionId)) {
          setLocallyPopped(false);
          popoutWindowRef.current = null;
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [locallyPopped, manager, interactionId]);

  const handlePopOut = useCallback(() => {
    const t = playerRef.current?.getState().currentTime ?? 0;
    const timeParam = t > 0 ? `?t=${t}` : "";
    const url = `/customers/${customerId}/v2/popout/${interactionId}${timeParam}`;
    const windowName = `popout-${interactionId}`;
    const features =
      "width=700,height=800,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes";
    const win = window.open(url, windowName, features);
    if (!win) {
      alert("Please allow pop-ups for this site to use the player in a separate window.");
      return;
    }
    popoutWindowRef.current = win;
    setLocallyPopped(true);
  }, [customerId, interactionId]);

  const handleDetachTab = useCallback(
    (tab: TabId) => {
      const url = `/customers/${customerId}/v2/popout/${interactionId}/tab/${tab}`;
      const win = window.open(url, `detached-${interactionId}-${tab}`, "width=600,height=700");
      if (!win) {
        alert("Please allow pop-ups for this site to detach tabs.");
        return;
      }
      manager.notifyDetachTab(interactionId, tab);
    },
    [customerId, interactionId, manager],
  );

  const handleFocusWindow = useCallback(() => {
    popoutWindowRef.current?.focus();
  }, []);

  const handlePullBack = useCallback(() => {
    // Save playback position so we can resume
    const playback = manager.playbackState;
    if (playback) {
      setResumeTime(playback.currentTime);
    }
    popoutWindowRef.current?.close();
    popoutWindowRef.current = null;
    setLocallyPopped(false);
  }, [manager.playbackState]);

  // Use a ref-based callback so WaveSurfer's stale closure always calls the latest version
  const timeUpdateRef = useRef<(time: number) => void>(() => {});
  timeUpdateRef.current = (time: number) => {
    setCurrentTimeSeconds(time);
    manager.broadcastPlayback(interactionId, {
      currentTime: time,
      duration: playerRef.current?.getState().duration ?? 0,
      isPlaying: playerRef.current?.getState().isPlaying ?? false,
      playbackRate: playerRef.current?.getState().playbackRate ?? 1,
    });
  };
  const handleTimeUpdate = useCallback((time: number) => {
    timeUpdateRef.current(time);
  }, []);

  const handlePlayerReady = useCallback(() => {
    // Resume playback position after pull-back
    if (resumeTime !== null && resumeTime > 0 && playerRef.current) {
      playerRef.current.seek(resumeTime);
      setResumeTime(null);
    }
  }, [resumeTime]);

  // Handle inbound playback commands from detached tabs.
  // Uses setOnPlaybackCommand once — the callback reads playerRef at call time
  // so it always targets the current player even after remounts.
  useEffect(() => {
    manager.setOnPlaybackCommand((cmd) => {
      const player = playerRef.current;
      if (!player) return;
      switch (cmd.action) {
        case "play": player.play(); break;
        case "pause": player.pause(); break;
        case "seek": player.seek(cmd.time); break;
        case "setRate": player.setRate(cmd.rate); break;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: metadata, isLoading: metadataLoading } = useInteractionMetadata(
    customerId,
    interactionId,
  );

  const hasMedia = MEDIA_TYPES.has(interactionType);
  const { data: fileUrlData, isLoading: mediaLoading } = useFileUrl(
    interactionId,
    customerId,
    hasMedia,
  );

  const { data: transcript, isLoading: transcriptLoading } = useTranscript(
    customerId,
    interactionId,
  );

  const [summaryRequested, setSummaryRequested] = useState(false);

  const { data: summaryInitial, isLoading: summaryLoading } = useGetSummary(
    customerId,
    interactionId,
  );

  // Poll when summary is in progress (either from initial load or after requesting)
  const shouldPoll =
    summaryRequested ||
    summaryInitial?.status === SummaryStatus.InProgress;

  const { data: summaryPolled } = useSummaryPolling(
    customerId,
    interactionId,
    shouldPoll,
  );

  // Use polled data when available, otherwise initial
  const summary = shouldPoll && summaryPolled ? summaryPolled : summaryInitial;

  // Stop polling once completed or failed
  const handleSummaryRequested = useCallback(() => {
    setSummaryRequested(true);
  }, []);

  // Derive header fields from metadata
  const agentName = (metadata?.agentName as string) ?? "";
  const startTime = formatTimestamp(metadata?.startTime as string | undefined);
  const duration = formatDuration(metadata?.durationMs as number | undefined);
  const phoneNumber = (metadata?.phoneNumber as string) ?? "";

  const dateTime = startTime || "";

  if (metadataLoading) {
    return (
      <Box
        sx={{
          width: DETAIL_PANE_WIDTH,
          minWidth: DETAIL_PANE_WIDTH,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "var(--bg-surface)",
          borderLeft: "1px solid var(--border-subtle)",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (locallyPopped) {
    return (
      <Box
        sx={{
          width: DETAIL_PANE_WIDTH,
          minWidth: DETAIL_PANE_WIDTH,
          height: "100%",
          backgroundColor: "var(--bg-surface)",
          borderLeft: "1px solid var(--border-subtle)",
        }}
      >
        <DetailPaneStub
          agentName={agentName || "\u2014"}
          interactionId={interactionId}
          onPullBack={handlePullBack}
          onFocusWindow={handleFocusWindow}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: DETAIL_PANE_WIDTH,
        minWidth: DETAIL_PANE_WIDTH,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--bg-surface)",
        borderLeft: "1px solid var(--border-subtle)",
        overflow: "hidden",
        animation: "slideInFromRight 200ms ease",
        "@keyframes slideInFromRight": {
          from: { transform: "translateX(100%)", opacity: 0 },
          to: { transform: "translateX(0)", opacity: 1 },
        },
      }}
    >
      {/* Header */}
      <DetailHeader
        agentName={agentName || "\u2014"}
        dateTime={dateTime || "\u2014"}
        duration={duration || "\u2014"}
        phoneNumber={phoneNumber || "\u2014"}
        onClose={onClose}
        onOpenInNewWindow={handlePopOut}
      />

      {/* Waveform player — only for media interaction types */}
      {hasMedia && (
        <WaveformPlayer
          ref={playerRef}
          mediaUrl={fileUrlData?.temporaryUrl}
          isLoading={mediaLoading}
          isTranscoding={!!fileUrlData?.transcodingJobId}
          interactionType={interactionType}
          redactionTimestamps={
            metadata?.redactionTimestamps as
              | { start: string; end: string }[]
              | undefined
          }
          autoPlay
          onReady={handlePlayerReady}
          onTimeUpdate={handleTimeUpdate}
        />
      )}

      {/* Tabs */}
      <DetailTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        detachedTabs={manager.getDetachedTabs(interactionId)}
        onDetachTab={handleDetachTab}
      />

      {/* Tab content — scrollable */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {activeTab === "transcript" && (
          <TranscriptTab
            customerId={customerId}
            interactionId={interactionId}
            interactionType={interactionType}
            hasMedia={hasMedia}
            transcript={transcript ?? undefined}
            isLoading={transcriptLoading}
            currentTimeSeconds={currentTimeSeconds}
          />
        )}
        {activeTab === "summary" && (
          <SummaryTab
            customerId={customerId}
            interactionId={interactionId}
            summary={summary ?? undefined}
            isLoading={summaryLoading}
            hasTranscript={!!transcript}
            hasMedia={hasMedia}
            interactionType={interactionType}
            onSummaryRequested={handleSummaryRequested}
          />
        )}
        {activeTab === "metadata" && (
          <MetadataTab metadata={metadata ?? {}} />
        )}
      </Box>

      {/* Archie context bar (feature-gated) */}
      <ArchieContextBar />
    </Box>
  );
}
